// The tutorial (DEMO.md): chapters you can enter, leave and re-enter, each one
// standalone and identical every time.
//
// The shape that makes that true: entering a chapter turns on the sim's
// tutorial mode, which rebuilds the world from the seed alone and stops the
// journal being read or written. So whatever the visitor did last time — or
// whatever leagues a signed-in manager actually has — is neither shown nor
// disturbed, and leaving puts their own world back.
//
// Screens mark anchors and report actions; they never know about the tutorial
// otherwise.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Icon } from '../kit/Icon';
import { Ring } from '../kit/overlays';
import { Button } from '../kit/ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../data/session';
import { useSim } from '../data/SimProvider';
import { CHAPTER_KEY, useSetChapterOpen } from '../data/tutorialChapter';
import { useChatDrawer } from '../shell/chatDrawer';
import { useTier } from '../shell/useTier';
import {
  CHAPTERS,
  chapterById,
  markChapterSeen,
  onTourAction,
  seenChapters,
  type Chapter,
  type Moment,
} from './chapters';
import '../styles/tour.css';

const KEY = CHAPTER_KEY;
/** Where the chapter index lives. Reachable signed in or not. */
export const INDEX_ROUTE = '/demo';

interface At {
  chapterId: string;
  step: number;
}

function readAt(): At | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as Partial<At> | null;
    if (!v || typeof v.chapterId !== 'string' || typeof v.step !== 'number') return null;
    const chapter = chapterById(v.chapterId);
    if (!chapter) return null;
    return {
      chapterId: v.chapterId,
      step: Math.max(0, Math.min(chapter.moments.length - 1, v.step)),
    };
  } catch {
    return null;
  }
}

function writeAt(at: At | null): void {
  try {
    if (at) sessionStorage.setItem(KEY, JSON.stringify(at));
    else sessionStorage.removeItem(KEY);
  } catch {
    // Storage blocked: the chapter lasts for this page load.
  }
}

interface TutorialValue {
  /** The chapter being walked, if any. */
  chapter: Chapter | null;
  moment: Moment | null;
  step: number;
  /** Chapters this account has finished. Always empty for a visitor. */
  seen: Set<string>;
  /** Open the index. Turns tutorial mode on so the demo leagues are what shows. */
  openIndex: () => void;
  enterChapter: (id: string) => void;
  next: () => void;
  back: () => void;
  /** Leave the chapter, back to the index. */
  toIndex: () => void;
  /** Leave the tutorial entirely: the manager's own world comes back. */
  exit: () => void;
}

const TutorialContext = createContext<TutorialValue | null>(null);

export function TutorialProvider({ children }: { children: ReactNode }) {
  const [at, setAt] = useState<At | null>(() => {
    // Loading on the front door means you left. Without this the stored
    // chapter is restored and its moment navigates you straight back into it,
    // which is the trap all over again: typing the address of the front page
    // and being bounced somewhere else.
    const path = typeof window === 'undefined' ? '' : window.location.pathname;
    if (path === '/' || path === '/welcome') {
      writeAt(null);
      return null;
    }
    return readAt();
  });
  const [nice, setNice] = useState(false);
  // WHETHER A CHAPTER IS OPEN, for whatever chooses a league (B-527).
  const setChapterOpen = useSetChapterOpen();
  useLayoutEffect(() => setChapterOpen(at !== null), [at, setChapterOpen]);
  const { session } = useSession();
  const sim = useSim();
  const navigate = useNavigate();
  const location = useLocation();
  const chat = useChatDrawer();

  const chapter = at ? chapterById(at.chapterId) : null;
  const moment = chapter ? (chapter.moments[at!.step] ?? null) : null;

  const simRef = useRef(sim);
  simRef.current = sim;
  const chatRef = useRef(chat);
  chatRef.current = chat;
  const navRef = useRef(navigate);
  navRef.current = navigate;

  const [seen, setSeen] = useState<Set<string>>(() => seenChapters(session?.userId ?? null));
  useEffect(() => {
    setSeen(seenChapters(session?.userId ?? null));
  }, [session?.userId]);

  /**
   * Land the current moment: clock, route, chat drawer. Keyed on the moment so
   * it runs once when the chapter or step changes, and never fights a visitor
   * who has wandered off to look at something else.
   */
  const landed = useRef<string | null>(null);
  useEffect(() => {
    if (!chapter || !moment) {
      landed.current = null;
      return;
    }
    const key = `${chapter.id}:${moment.id}`;
    if (landed.current === key) return;
    landed.current = key;
    setNice(false);
    const s = simRef.current;
    if (moment.clock) s.jump(moment.clock);
    s.setSpeed(30);
    s.setPlaying(Boolean(moment.playing));
    navRef.current(moment.route);
    chatRef.current.setOpen(Boolean(moment.openChat));
  }, [chapter, moment]);

  const openIndex = useCallback(() => {
    if (!simRef.current.tutorial) simRef.current.setTutorial(true);
    setAt(null);
    writeAt(null);
    chatRef.current.setOpen(false);
    navRef.current(INDEX_ROUTE);
  }, []);

  const enterChapter = useCallback((id: string) => {
    const c = chapterById(id);
    if (!c) return;
    // Always rebuild on the way in: that is what makes a chapter the same
    // every time, however the last visit ended.
    simRef.current.setTutorial(true);
    const next = { chapterId: id, step: 0 };
    setAt(next);
    writeAt(next);
  }, []);

  const toIndex = useCallback(() => {
    setAt(null);
    writeAt(null);
    chatRef.current.setOpen(false);
    simRef.current.setPlaying(false);
    navRef.current(INDEX_ROUTE);
  }, []);

  const finishChapter = useCallback(() => {
    if (chapter && session?.userId) {
      markChapterSeen(session.userId, chapter.id);
      setSeen(seenChapters(session.userId));
    }
    toIndex();
  }, [chapter, session?.userId, toIndex]);

  const next = useCallback(() => {
    if (!chapter || !at) return;
    if (at.step >= chapter.moments.length - 1) {
      finishChapter();
      return;
    }
    const nextAt = { chapterId: at.chapterId, step: at.step + 1 };
    setAt(nextAt);
    writeAt(nextAt);
  }, [chapter, at, finishChapter]);

  const back = useCallback(() => {
    if (!at) return;
    if (at.step === 0) {
      toIndex();
      return;
    }
    const nextAt = { chapterId: at.chapterId, step: at.step - 1 };
    setAt(nextAt);
    writeAt(nextAt);
  }, [at, toIndex]);

  const exit = useCallback(() => {
    setAt(null);
    writeAt(null);
    chatRef.current.setOpen(false);
    simRef.current.setPlaying(false);
    simRef.current.setTutorial(false);
    navRef.current('/');
  }, []);

  // "Nice." when the visitor does the thing the card asked for.
  useEffect(() => {
    if (!moment?.done) return;
    return onTourAction((action) => {
      if (action === moment.done) setNice(true);
    });
  }, [moment]);
  useEffect(() => {
    if (moment?.done === 'open-league' && /^\/l\//.test(location.pathname)) setNice(true);
  }, [moment, location.pathname]);

  const value = useMemo<TutorialValue>(
    () => ({
      chapter,
      moment,
      step: at?.step ?? 0,
      seen,
      openIndex,
      enterChapter,
      next,
      back,
      toIndex,
      exit,
    }),
    [chapter, moment, at?.step, seen, openIndex, enterChapter, next, back, toIndex, exit],
  );

  return (
    <TutorialContext.Provider value={value}>
      {children}
      {chapter && moment ? (
        <ChapterCard
          chapter={chapter}
          moment={moment}
          step={at!.step}
          nice={nice}
          onNext={next}
          onBack={back}
          onIndex={toIndex}
          onJump={(preset) => {
            simRef.current.jump(preset);
            simRef.current.setPlaying(false);
          }}
        />
      ) : null}
    </TutorialContext.Provider>
  );
}

export function useTutorial(): TutorialValue {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error('useTutorial must be used inside TutorialProvider');
  return ctx;
}

/** Every chapter, for the index screen. */
export { CHAPTERS };

/** A rounded outline over the anchor, nothing dimmed; follows the element as layouts shift. */
function Spotlight({ selector }: { selector: string }) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      const el = document.querySelector<HTMLElement>(selector);
      const r = el?.getBoundingClientRect() ?? null;
      setRect(r && r.width > 0 && r.height > 0 ? r : null);
    };
    measure();
    const id = setInterval(measure, 500);
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      clearInterval(id);
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [selector]);
  if (!rect) return null;
  return <Ring rect={rect} className="fsp-tour-spot" />;
}

function ChapterCard({
  chapter,
  moment,
  step,
  nice,
  onNext,
  onBack,
  onIndex,
  onJump,
}: {
  chapter: Chapter;
  moment: Moment;
  step: number;
  nice: boolean;
  onNext: () => void;
  onBack: () => void;
  onIndex: () => void;
  onJump: (preset: NonNullable<Moment['jump']>['preset']) => void;
}) {
  const tier = useTier();
  const phone = tier === 'phone';
  const last = step === chapter.moments.length - 1;
  return (
    <>
      {moment.anchor ? <Spotlight selector={moment.anchor} /> : null}
      <aside
        className="fsp-tour-card"
        data-phone={phone || undefined}
        data-tablet={tier === 'tablet' || undefined}
        aria-label={`${chapter.title}, step ${step + 1} of ${chapter.moments.length}`}
        data-tour-step={moment.id}
        data-tour-chapter={chapter.id}
      >
        <div className="fsp-tour-dots" aria-hidden>
          {chapter.moments.map((m, i) => (
            <span
              key={m.id}
              className={`fsp-tour-dot ${i === step ? 'on' : i < step ? 'done' : ''}`}
            />
          ))}
        </div>
        <div className="fsp-tour-track">
          {chapter.title} · {step + 1} of {chapter.moments.length}
        </div>
        <div className="fsp-tour-title">{moment.title}</div>
        <div className="fsp-tour-body">{moment.body}</div>
        <div className="fsp-tour-try">
          <b>Try it</b>
          <span>{moment.tryIt}</span>
          {nice ? <span className="fsp-tour-nice">Nice.</span> : null}
        </div>
        {moment.jump ? (
          <Button size="xs" icon="play-at" onClick={() => onJump(moment.jump!.preset)}>
            {moment.jump.label}
          </Button>
        ) : null}
        <div className="fsp-tour-actions">
          <Button size="xs" variant="quiet" icon="back" onClick={onBack}>
            Back
          </Button>
          <Button
            size="xs"
            variant="primary"
            iconRight={last ? undefined : 'forward'}
            onClick={onNext}
            hook="data-tour-next"
          >
            {last ? 'Done' : 'Next'}
          </Button>
          <button
            type="button"
            className="fsp-linklike fsp-tour-skip"
            onClick={onIndex}
            data-tour-index
          >
            <Icon name="list" size="sm" /> All chapters
          </button>
        </div>
      </aside>
    </>
  );
}
